export interface CurrencyConfig {
  symbol: string;
  position: 'before' | 'after';
  format: (value: number) => string;
}

export const getCurrencyConfig = (country?: string): CurrencyConfig => {
  //if position is "before" means the currency symbol is before the value, if "after" means the currency symbol is after the value
  switch (country?.toLowerCase()) {
    case 'co':
      return {
        symbol: '$',
        position: 'before',
        format: (value: number) =>
          `$ ${value.toLocaleString('es-ES', { useGrouping: true })}`,
      };
    case 'es':
    default:
      return {
        symbol: '€',
        position: 'after',
        format: (value: number) =>
          `${value.toLocaleString('es-ES', { useGrouping: true })} €`,
      };
    case 'mx':
      return {
        symbol: 'MX',
        position: 'before',
        format: (value: number) =>
          `MX ${value.toLocaleString('es-MX', { useGrouping: true })}`,
      };
    case 'pe':
      return {
        symbol: 'S/',
        position: 'before',
        format: (value: number) =>
          `S/ ${value.toLocaleString('es-PE', { useGrouping: true })}`,
      };
  }
};

// Función específica para formatear precios en ticks de gráficas (sin espacios problemáticos)
// Usa espacios no separables para mantener el texto en una sola línea
export const formatTickPrice = (
  value: number,
  country?: string,
  listingType?: string,
  sqmSuffix?: string
): string => {
  const config = getCurrencyConfig(country);
  const nonBreakingSpace = '\u00A0'; // Espacio no separable

  if (listingType === '7' || listingType === '11') {
    // Para venta/alquiler, usar formato simple reemplazando espacios normales por no separables
    const formattedValue = formatCurrencyExtended(
      value,
      country,
      false,
      sqmSuffix,
      undefined,
      true,
      1000000,
      false,
      false
    ).replace(/\s/g, nonBreakingSpace);
    return formattedValue;
  } else {
    // Para precio por m², usar formato con sufijo reemplazando espacios normales por no separables
    const formattedValue = formatCurrencyExtended(
      value,
      country,
      true,
      sqmSuffix,
      undefined,
      true,
      1000000,
      false,
      false
    ).replace(/\s/g, nonBreakingSpace);
    return `${formattedValue}`;
  }
};

// Función para obtener solo el símbolo de la moneda según el país
// export const getCurrencySymbol = (country?: string): string => {
//   const config = getCurrencyConfig(country);
//   return config.symbol;
// };

export const formatCurrencyExtended = (
  value: number,
  country?: string,
  sqm: boolean = false,
  sqmSuffix: string = '/m²',
  montText?: string,
  round: boolean = false,
  large: number = 1000000,
  only_suffix: boolean = false,
  only_value: boolean = false
): string => {
  const config = getCurrencyConfig(country);

  let formattedValue = config.format(value);

  // Si `large` es true y el valor es mayor a 1M, reemplaza el número por uno con "M"
  if (large && value >= large) {
    const millions = round
      ? Math.round(value / large)
      : (value / large).toFixed(2);
    formattedValue =
      config.position === 'before'
        ? `${config.symbol} ${millions}M`
        : `${millions}M ${config.symbol}`;
  } else {
    // Para valores normales, aplicar el formato de decimales según el parámetro round
    if (round) {
      // Redondear el valor
      const roundedValue = Math.round(value);
      formattedValue = config.format(roundedValue);
    } else {
      // Reemplazar el valor en el string formateado
      const numericRegex = /[\d.,]+M?/;
      const match = formattedValue.match(numericRegex);
      if (match) {
        const numericIndex = formattedValue.indexOf(match[0]);
        const beforeNumeric = formattedValue.substring(0, numericIndex);
        const afterNumeric = formattedValue.substring(
          numericIndex + match[0].length
        );

        // Mostrar 2 decimales
        const decimalValue = value.toFixed(2);
        // Formatear el valor con 2 decimales según el país
        const formattedDecimal = parseFloat(decimalValue).toLocaleString(
          country?.toLowerCase() === 'mx' ? 'es-MX' : 'es-ES',
          { useGrouping: true }
        );

        formattedValue = beforeNumeric + formattedDecimal + afterNumeric;
      }
    }
  }

  // Si sqm es true, añade el sufijo sqmSuffix
  if (sqm) {
    formattedValue += sqmSuffix;
  }

  // Si montText tiene valor, añadirlo al final
  if (montText) {
    formattedValue += `${montText}`;
  }

  // Lógica especial: si round=true y sqm=true y hay una M, redondear a dos decimales
  if (round && formattedValue.includes('M')) {
    const millions = (value / 1000000).toFixed(2);
    const numericRegex = /[\d.,]+M/;
    const match = formattedValue.match(numericRegex);
    if (match) {
      const numericIndex = formattedValue.indexOf(match[0]);
      const beforeNumeric = formattedValue.substring(0, numericIndex);
      const afterNumeric = formattedValue.substring(
        numericIndex + match[0].length
      );

      formattedValue = beforeNumeric + millions + 'M' + afterNumeric;
    }
  }

  if (only_suffix) {
    // Usar regex para encontrar el valor numérico y eliminar ese valor y todo lo que esté a su izquierda
    // Busca números (incluyendo decimales, comas, puntos y M para millones)
    const numericRegex = /[\d.,]+M?/;
    const match = formattedValue.match(numericRegex);

    if (match) {
      // Encuentra la posición del valor numérico
      const numericIndex = formattedValue.indexOf(match[0]);
      // Devuelve solo la parte después del valor numérico
      return formattedValue.substring(numericIndex + match[0].length).trim();
    }

    return '';
  }

  if (only_value) {
    // Usar regex para encontrar el valor numérico y eliminar todo lo que esté a su derecha
    // Busca números (incluyendo decimales, comas, puntos y M para millones)
    const numericRegex = /[\d.,]+M?/;
    const match = formattedValue.match(numericRegex);

    if (match) {
      // Encuentra la posición del valor numérico
      const numericIndex = formattedValue.indexOf(match[0]);
      // Devuelve solo la parte hasta el final del valor numérico
      return formattedValue.substring(0, numericIndex + match[0].length).trim();
    }

    return '';
  }

  return formattedValue;
};
